<template>
  <section id="subscription-renew">
    <!-- Mobile Header -->
    <div class="custom-mobile-header d-md-none">
      <div class="header-content">
        <button
          type="button"
          class="back-button"
          @click="goBack"
        >
          <v-icon size="25" color="#fff">mdi-arrow-right-bold</v-icon>
        </button>
        <div class="header-title">تمدید اشتراک</div>
      </div>
    </div>

    <b-card class="main-card">
      <!-- Loading -->
      <div v-if="loading" class="text-center py-5">
        <b-spinner variant="primary" />
        <p class="mt-2">در حال بارگذاری...</p>
      </div>

      <!-- Gateway Disabled -->
      <div v-else-if="!gatewayEnabled" class="gateway-disabled text-center py-5">
        <div class="disabled-icon mb-4">
          <v-icon size="80" color="grey">mdi-credit-card-off</v-icon>
        </div>
        <h4 class="mb-3">درگاه پرداخت غیرفعال است</h4>
        <p class="text-muted mb-4">
          در حال حاضر امکان پرداخت آنلاین وجود ندارد.<br>
          لطفاً بعداً مراجعه کنید یا با پشتیبانی تماس بگیرید.
        </p>
        <b-button variant="outline-primary" @click="goBack">
          بازگشت
        </b-button>
      </div>

      <!-- Main Content -->
      <div v-else class="content-wrapper">
        <!-- Page Header -->
        <div class="page-header mb-4">
          <div class="d-flex align-items-center">
            <div class="header-icon ml-3">
              <v-icon size="24" color="white">mdi-credit-card-refresh</v-icon>
            </div>
            <div>
              <h4 class="mb-1">تمدید اشتراک</h4>
              <p class="text-muted mb-0">دستگاه خود را انتخاب کنید و پلن مورد نظر را برگزینید</p>
            </div>
          </div>
        </div>

        <div class="content-grid">
          <!-- Main Content -->
          <div class="devices-section">
            <!-- Device Selection -->
            <div class="section-card mb-4">
              <div class="section-header">
                <h5 class="mb-0">
                  <v-icon size="20" class="ml-2">mdi-cellphone-link</v-icon>
                  انتخاب دستگاه
                </h5>
              </div>
              <div class="section-body">
                <div v-if="devices.length === 0" class="text-center py-4 text-muted">
                  هیچ دستگاهی یافت نشد
                </div>
                <div v-else class="devices-grid">
                  <div
                    v-for="device in devices"
                    :key="device._id"
                    class="device-card"
                    :class="{ selected: selectedDevice && selectedDevice._id === device._id }"
                    @click="selectDevice(device)"
                  >
                    <div class="device-icon">
                      <v-icon size="32" :color="selectedDevice && selectedDevice._id === device._id ? '#0ea5e9' : '#64748b'">
                        {{ getDeviceIcon(device.category) }}
                      </v-icon>
                    </div>
                    <div class="device-info">
                      <h6 class="device-title">{{ device.title || device.imei }}</h6>
                      <span class="device-imei">{{ device.imei }}</span>
                      <div class="device-status mt-2">
                        <b-badge :variant="device.disabled ? 'danger' : 'success'" pill>
                          {{ device.disabled ? 'غیرفعال' : 'فعال' }}
                        </b-badge>
                        <span v-if="device.expireTime" class="expire-date mr-2">
                          انقضا: {{ device.expireTime }}
                        </span>
                      </div>
                    </div>
                    <div class="device-check">
                      <v-icon v-if="selectedDevice && selectedDevice._id === device._id" color="#0ea5e9">
                        mdi-check-circle
                      </v-icon>
                      <v-icon v-else color="#cbd5e1">mdi-circle-outline</v-icon>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Plan Selection -->
            <div v-if="selectedDevice" class="section-card mb-4">
              <div class="section-header">
                <h5 class="mb-0">
                  <v-icon size="20" class="ml-2">mdi-tag-multiple</v-icon>
                  انتخاب پلن
                </h5>
              </div>
              <div class="section-body">
                <div v-if="plans.length === 0" class="text-center py-4 text-muted">
                  هیچ پلنی یافت نشد
                </div>
                <div v-else class="plans-grid">
                  <div
                    v-for="plan in plans"
                    :key="plan._id"
                    class="plan-card"
                    :class="{ selected: selectedPlan && selectedPlan._id === plan._id }"
                    @click="selectPlan(plan)"
                  >
                    <div class="plan-header">
                      <h6 class="plan-title">{{ plan.title }}</h6>
                      <span class="plan-period">{{ plan.period }} ماهه</span>
                    </div>
                    <div class="plan-price">
                      <span class="price-amount">{{ formatPrice(getPlanPrice(plan)) }}</span>
                      <span class="price-unit">تومان</span>
                    </div>
                    <p v-if="plan.description" class="plan-description">{{ plan.description }}</p>
                    <div class="plan-check">
                      <v-icon v-if="selectedPlan && selectedPlan._id === plan._id" color="#0ea5e9">
                        mdi-check-circle
                      </v-icon>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Payment Summary -->
            <div v-if="selectedDevice && selectedPlan" class="section-card payment-summary">
              <div class="section-header">
                <h5 class="mb-0">
                  <v-icon size="20" class="ml-2">mdi-receipt</v-icon>
                  خلاصه پرداخت
                </h5>
              </div>
              <div class="section-body">
                <div class="summary-row">
                  <span>دستگاه:</span>
                  <span class="font-weight-bold">{{ selectedDevice.title || selectedDevice.imei }}</span>
                </div>
                <div class="summary-row">
                  <span>پلن:</span>
                  <span class="font-weight-bold">{{ selectedPlan.title }} ({{ selectedPlan.period }} ماهه)</span>
                </div>
                <div class="summary-row total">
                  <span>مبلغ قابل پرداخت:</span>
                  <span class="total-amount">{{ formatPrice(totalAmount) }} تومان</span>
                </div>

                <b-button
                  variant="primary"
                  block
                  class="pay-button mt-4"
                  :disabled="paymentLoading"
                  @click="initiatePayment"
                >
                  <b-spinner v-if="paymentLoading" small class="ml-2" />
                  <v-icon v-else size="20" class="ml-2">mdi-credit-card</v-icon>
                  پرداخت و تمدید اشتراک
                </b-button>
              </div>
            </div>
          </div>

          <!-- Sidebar -->
          <div class="sidebar">
            <!-- Recent Transactions -->
            <div class="section-card mb-4">
              <div class="section-header">
                <h5 class="mb-0">
                  <v-icon size="20" class="ml-2">mdi-history</v-icon>
                  تراکنش‌های اخیر
                </h5>
              </div>
              <div class="section-body">
                <div v-if="recentTransactions.length === 0" class="text-center py-3 text-muted">
                  تراکنشی یافت نشد
                </div>
                <div v-else class="transactions-list">
                  <div
                    v-for="tx in recentTransactions"
                    :key="tx._id"
                    class="transaction-item"
                  >
                    <div class="tx-icon" :class="tx.status">
                      <v-icon size="16" color="white">
                        {{ tx.status === 'success' ? 'mdi-check' : tx.status === 'failed' ? 'mdi-close' : 'mdi-clock-outline' }}
                      </v-icon>
                    </div>
                    <div class="tx-info">
                      <span class="tx-amount">{{ formatPrice(tx.amount / 10) }} تومان</span>
                      <span class="tx-date">{{ formatDate(tx.creationTime) }}</span>
                    </div>
                  </div>
                </div>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  block
                  class="mt-3"
                  @click="$router.push({ name: 'payment-transactions' })"
                >
                  مشاهده همه تراکنش‌ها
                </b-button>
              </div>
            </div>

            <!-- Help Card -->
            <div class="section-card help-card">
              <div class="section-header">
                <h5 class="mb-0">
                  <v-icon size="20" class="ml-2">mdi-help-circle</v-icon>
                  راهنمای پرداخت
                </h5>
              </div>
              <div class="section-body">
                <ul class="help-list">
                  <li>پس از انتخاب دستگاه و پلن، به درگاه بانک ملی هدایت می‌شوید</li>
                  <li>پرداخت از طریق کارت‌های عضو شتاب امکان‌پذیر است</li>
                  <li>پس از پرداخت موفق، اشتراک به صورت خودکار تمدید می‌شود</li>
                  <li>در صورت بروز مشکل با پشتیبانی تماس بگیرید</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-card>

    <!-- Footer for Mobile -->
    <footer class="mobile-footer d-md-none">
      <v-list dense>
        <v-list-item-group v-model="selectedFooterItem" color="primary">
          <v-list-item
            v-for="item in footerItems"
            :key="item.keyItem"
            @click="$router.push({ name: item.rout })"
          >
            <v-list-item-icon>
              <v-icon v-text="item.icon" />
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title v-text="item.text" />
            </v-list-item-content>
          </v-list-item>
        </v-list-item-group>
      </v-list>
    </footer>
  </section>
</template>

<script>
import {
  BCard, BButton, BBadge, BSpinner,
} from 'bootstrap-vue'
import PaymentService from '@/services/payment.service'
import DeviceService from '@/services/device.service'
import PlanService from '@/services/plan.service'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BButton,
    BBadge,
    BSpinner,
  },
  data() {
    return {
      loading: true,
      paymentLoading: false,
      gatewayEnabled: true,
      devices: [],
      plans: [],
      recentTransactions: [],
      selectedDevice: null,
      selectedPlan: null,
      selectedFooterItem: 0,
      footerItems: [
        { keyItem: 0, text: 'خانه', icon: 'mdi-home', rout: 'home' },
        { keyItem: 1, text: 'دستگاه‌ها', icon: 'mdi-cellphone-link', rout: 'device-list' },
        { keyItem: 2, text: 'پروفایل', icon: 'mdi-account', rout: 'profile' },
      ],
    }
  },
  computed: {
    totalAmount() {
      if (!this.selectedPlan) return 0
      return this.getPlanPrice(this.selectedPlan)
    },
  },
  async mounted() {
    await this.initialize()

    // اگر از query پارامتر دستگاه آمده، آن را انتخاب کن
    if (this.devices && this.devices.length > 0) {
      if (this.$route.query.deviceId) {
        const device = this.devices.find(d => d._id === this.$route.query.deviceId)
        if (device) {
          this.selectDevice(device)
        }
      } else if (this.$route.query.imei) {
        const device = this.devices.find(d => d.imei === this.$route.query.imei)
        if (device) {
          this.selectDevice(device)
        }
      }
    }
  },
  methods: {
    async initialize() {
      try {
        this.loading = true

        // بررسی وضعیت درگاه
        const statusRes = await PaymentService.getStatus()
        if (statusRes.status === 'success') {
          this.gatewayEnabled = statusRes.result.enabled
        }

        if (!this.gatewayEnabled) {
          this.loading = false
          return
        }

        // دریافت دستگاه‌ها
        const devicesRes = await DeviceService.getList()
        if (devicesRes.status === 'success') {
          // API ممکنه result.data یا result برگردونه
          const deviceData = devicesRes.result.data || devicesRes.result || []
          this.devices = Array.isArray(deviceData) ? deviceData : []
        }

        // دریافت پلن‌ها
        const plansRes = await PlanService.getList()
        if (plansRes.status === 'success') {
          // API ممکنه result.data یا result برگردونه
          const planData = plansRes.result.data || plansRes.result || []
          this.plans = Array.isArray(planData) ? planData.filter(p => !p.disabled) : []
        }

        // دریافت تراکنش‌های اخیر
        const txRes = await PaymentService.getList({ limit: 5 })
        if (txRes.status === 'success') {
          this.recentTransactions = txRes.result.payments || []
        }
      } catch (error) {
        console.error('Error initializing:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'خطا',
            text: 'خطا در بارگذاری اطلاعات',
            icon: 'XIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },

    selectDevice(device) {
      this.selectedDevice = device
      // انتخاب خودکار اولین پلن
      if (this.plans.length > 0 && !this.selectedPlan) {
        this.selectedPlan = this.plans[0]
      }
    },

    selectPlan(plan) {
      this.selectedPlan = plan
    },

    async initiatePayment() {
      if (!this.selectedDevice || !this.selectedPlan) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'خطا',
            text: 'لطفاً دستگاه و پلن را انتخاب کنید',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      try {
        this.paymentLoading = true

        const res = await PaymentService.requestPayment({
          deviceId: this.selectedDevice._id,
          planId: this.selectedPlan._id,
        })

        if (res.status === 'success') {
          // ریدایرکت به درگاه بانک
          window.location.href = res.result.paymentUrl
        } else {
          throw new Error(res.message || 'خطا در ایجاد درخواست پرداخت')
        }
      } catch (error) {
        console.error('Payment error:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'خطا',
            text: error.message || 'خطا در ایجاد درخواست پرداخت',
            icon: 'XIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.paymentLoading = false
      }
    },

    getDeviceIcon(category) {
      const icons = {
        car: 'mdi-car',
        motorcycle: 'mdi-motorbike',
        truck: 'mdi-truck',
        bus: 'mdi-bus',
        person: 'mdi-walk',
        pet: 'mdi-paw',
        default: 'mdi-map-marker',
      }
      return icons[category] || icons.default
    },

    getPlanPrice(plan) {
      // قیمت از ریال به تومان تبدیل می‌شود
      if (plan.price && plan.price > 0) {
        return plan.price / 10
      }
      return 0
    },

    formatPrice(amount) {
      return new Intl.NumberFormat('fa-IR').format(amount)
    },

    formatDate(timestamp) {
      if (!timestamp) return '-'
      return new Date(timestamp * 1000).toLocaleDateString('fa-IR')
    },

    goBack() {
      this.$router.go(-1)
    },
  },
}
</script>

<style scoped>
#subscription-renew {
  padding-bottom: 80px;
}

.main-card {
  background: #f8fafc !important;
  border: none !important;
  min-height: 100vh;
}

.page-header {
  padding: 20px;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  border-radius: 16px;
  color: white;
}

.page-header h4,
.page-header p {
  color: white;
}

.page-header p {
  opacity: 0.9;
}

.header-icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.content-grid {
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 24px;
}

.section-card {
  background: white;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.section-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.section-header h5 {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.section-body {
  padding: 20px;
}

/* Devices Grid */
.devices-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.device-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.device-card:hover {
  border-color: #0ea5e9;
  background: #f0f9ff;
}

.device-card.selected {
  border-color: #0ea5e9;
  background: #e0f2fe;
}

.device-icon {
  width: 56px;
  height: 56px;
  background: #f1f5f9;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.device-info {
  flex: 1;
  min-width: 0;
}

.device-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
}

.device-imei {
  font-family: monospace;
  font-size: 12px;
  color: #64748b;
}

.device-status {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.expire-date {
  font-size: 11px;
  color: #64748b;
}

.device-check {
  flex-shrink: 0;
}

/* Plans Grid */
.plans-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
}

.plan-card {
  position: relative;
  padding: 20px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
}

.plan-card:hover {
  border-color: #0ea5e9;
  background: #f0f9ff;
}

.plan-card.selected {
  border-color: #0ea5e9;
  background: #e0f2fe;
}

.plan-header {
  margin-bottom: 12px;
}

.plan-title {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
}

.plan-period {
  font-size: 12px;
  color: #64748b;
  background: #f1f5f9;
  padding: 2px 8px;
  border-radius: 12px;
}

.plan-price {
  margin-bottom: 8px;
}

.price-amount {
  font-size: 24px;
  font-weight: 700;
  color: #0ea5e9;
}

.price-unit {
  font-size: 12px;
  color: #64748b;
  margin-right: 4px;
}

.plan-description {
  font-size: 12px;
  color: #64748b;
  margin: 0;
}

.plan-check {
  position: absolute;
  top: 12px;
  left: 12px;
}

/* Payment Summary */
.payment-summary {
  border: 2px solid #0ea5e9;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #e2e8f0;
  font-size: 14px;
}

.summary-row:last-of-type {
  border-bottom: none;
}

.summary-row.total {
  padding-top: 16px;
  margin-top: 8px;
  border-top: 2px solid #e2e8f0;
  border-bottom: none;
}

.total-amount {
  font-size: 20px;
  font-weight: 700;
  color: #0ea5e9;
}

.pay-button {
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  border: none;
  padding: 14px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 12px;
}

/* Sidebar */
.transactions-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.transaction-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.tx-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tx-icon.success {
  background: #22c55e;
}

.tx-icon.failed {
  background: #ef4444;
}

.tx-icon.pending {
  background: #f59e0b;
}

.tx-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.tx-amount {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
}

.tx-date {
  font-size: 11px;
  color: #64748b;
}

/* Help Card */
.help-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.help-list li {
  position: relative;
  padding: 8px 0 8px 20px;
  font-size: 13px;
  color: #475569;
  border-bottom: 1px solid #f1f5f9;
}

.help-list li:last-child {
  border-bottom: none;
}

.help-list li::before {
  content: '';
  position: absolute;
  right: 0;
  top: 14px;
  width: 6px;
  height: 6px;
  background: #0ea5e9;
  border-radius: 50%;
}

/* Mobile Header */
.custom-mobile-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  z-index: 999;
  display: flex;
  align-items: center;
  padding: 0 15px;
}

.custom-mobile-header .header-content {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.custom-mobile-header .back-button {
  position: absolute;
  right: 0;
  background: none;
  border: none;
  padding: 5px;
}

.custom-mobile-header .header-title {
  color: white;
  font-size: 18px;
  font-weight: 600;
}

/* Mobile Footer */
.mobile-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid #e2e8f0;
  z-index: 999;
}

/* Responsive */
@media (max-width: 991px) {
  .content-grid {
    grid-template-columns: 1fr;
  }

  .sidebar {
    order: 2;
    margin-top: 24px;
  }
}

@media (max-width: 768px) {
  #subscription-renew {
    margin: -28px;
    padding-top: 60px;
  }

  .main-card {
    border-radius: 0 !important;
  }

  .page-header {
    border-radius: 0;
    margin: -20px -20px 20px -20px;
    padding: 16px;
  }

  .devices-grid {
    grid-template-columns: 1fr;
  }

  .plans-grid {
    grid-template-columns: 1fr 1fr;
  }

  .device-card {
    padding: 12px;
  }

  .plan-card {
    padding: 16px;
  }

  .price-amount {
    font-size: 20px;
  }
}
</style>
